import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { QrCode, Copy, CheckCircle2, Info, Smartphone, Wallet, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { db, updateDoc, doc, getDoc } from '@/firebase';

// Helper for CRC16 CCITT
const crc16 = (data: string): string => {
  let crc = 0xFFFF;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
};

// Helper to generate a real QRIS string with amount
const generateQRIS = (amount: number, orderId: string, merchantData: string) => {
  let baseQRIS = merchantData.trim();
  
  // If not a full QRIS payload, use a default template
  if (!baseQRIS.startsWith('000201')) {
    const mId = baseQRIS || 'ID1020210123456';
    baseQRIS = `00020101021226590011ID.CO.QRIS.WWW0215${mId}0303UMI5204599953033605802ID5908AZMAN2046007JAKARTA62200708${orderId.slice(0, 10)}`;
  } else {
    // If it's a full payload, strip the existing CRC (last 8 chars: 6304XXXX)
    if (baseQRIS.includes('6304')) {
      baseQRIS = baseQRIS.split('6304')[0];
    }
  }

  // Add/Update Amount (Tag 54)
  const amountStr = amount.toString();
  const amountTag = `54${amountStr.length.toString().padStart(2, '0')}${amountStr}`;
  
  // Check if tag 54 already exists in base (unlikely for static QRIS)
  if (baseQRIS.includes('54')) {
    // Simple replacement if it exists (might be risky without full parser, but usually works for simple payloads)
    // Better to just append it before the CRC if we stripped the end
  }
  
  // Append amount and other necessary tags if missing
  // Tag 58 (Country Code), 59 (Merchant Name), 60 (Merchant City) are usually already there
  
  const fullPayload = `${baseQRIS}${amountTag}6304`;
  const crc = crc16(fullPayload);
  
  return `${fullPayload}${crc}`;
};

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id: string;
    productName: string;
    amount: number;
  } | null;
}

export default function PaymentModal({ isOpen, onClose, order }: PaymentModalProps) {
  const [paymentSettings, setPaymentSettings] = React.useState({
    danaBusinessId: '',
    merchantName: 'AZMAN 204'
  });

  React.useEffect(() => {
    if (!isOpen) return;

    const fetchSettings = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'payment'));
        if (settingsSnap.exists()) {
          setPaymentSettings(settingsSnap.data() as any);
        }
      } catch (error) {
        console.error('Error fetching payment settings:', error);
      }
    };

    fetchSettings();
  }, [isOpen]);

  if (!order) return null;

  const handleConfirmPayment = async () => {
    try {
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, { 
        status: 'awaiting_confirmation',
        paymentConfirmedAt: new Date(),
        updatedAt: new Date()
      });
      toast.success("Konfirmasi pembayaran telah dikirim. Admin akan segera memverifikasi.");
      onClose();
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error("Gagal mengirim konfirmasi. Silakan coba lagi.");
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} berhasil disalin!`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-card border-none shadow-2xl rounded-3xl overflow-hidden p-0 max-h-[95vh] flex flex-col">
        <div className="bg-primary p-6 text-primary-foreground shrink-0">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary-foreground">Pembayaran QRIS</DialogTitle>
            <DialogDescription className="text-primary-foreground/60">
              Silakan selesaikan pembayaran untuk pesanan Anda.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          <div className="text-center space-y-2">
            <p className="text-sm text-foreground/40 uppercase font-bold tracking-widest">Total Tagihan</p>
            <h2 className="text-3xl font-black text-foreground">Rp {order.amount.toLocaleString('id-ID')}</h2>
            <div className="flex justify-center gap-2">
              <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                <Wallet className="h-3 w-3 mr-1" /> DANA QRIS
              </Badge>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gold/10 text-primary text-xs font-bold border border-gold/20">
                ID: {order.id.slice(0, 8)}
              </div>
            </div>
          </div>

          {/* QRIS Code Section */}
          <div className="relative max-w-[280px] mx-auto bg-white p-6 rounded-[2rem] border-2 border-border shadow-xl flex flex-col items-center">
            <div className="w-full flex justify-between items-center mb-4">
              <img src="https://upload.wikimedia.org/wikipedia/commons/a/a2/Logo_QRIS.svg" alt="QRIS" className="h-6" />
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-[10px] font-bold text-black/40">OTOMATIS TERISI</span>
              </div>
            </div>
            
            <div className="bg-white p-2 rounded-xl shadow-inner border border-black/5">
              {paymentSettings.danaBusinessId ? (
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(generateQRIS(order.amount, order.id, paymentSettings.danaBusinessId))}`} 
                  alt="QRIS QR Code"
                  className="w-48 h-48 object-contain"
                />
              ) : (
                <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center text-black/20">
                  <QrCode className="h-12 w-12" />
                </div>
              )}
            </div>

            <div className="mt-4 text-center">
              <p className="text-[10px] font-bold text-black/40 uppercase tracking-widest mb-1">Merchant</p>
              <p className="text-sm font-black text-black">{paymentSettings.merchantName} (DANA)</p>
            </div>

            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-4 py-1 rounded-full shadow-lg">
              SCAN UNTUK BAYAR
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-2xl border border-border">
              <h4 className="text-sm font-bold text-foreground mb-3 flex items-center">
                <Smartphone className="h-4 w-4 mr-2 text-primary" /> Cara Bayar (DANA/Lainnya):
              </h4>
              <ul className="text-xs text-foreground/60 space-y-2 list-disc pl-4">
                <li>Buka aplikasi <span className="font-bold text-blue-500">DANA</span> atau e-wallet lainnya.</li>
                <li>Klik menu <span className="font-bold text-foreground">Pindai/Pay</span>.</li>
                <li>Arahkan ke QR Code di atas.</li>
                <li><span className="text-primary font-bold">PENTING:</span> Nominal <span className="underline">Rp {order.amount.toLocaleString('id-ID')}</span> akan muncul otomatis.</li>
                <li>Klik Bayar dan simpan bukti transaksi.</li>
                <li><span className="font-bold text-green-500">Terakhir:</span> Kirimkan bukti pembayaran Anda melalui tombol <span className="font-bold">WhatsApp</span> di bawah ini.</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-2xl border border-green-500/20">
              <MessageCircle className="h-5 w-5 text-green-500 shrink-0" />
              <p className="text-xs text-foreground/70 leading-relaxed">
                Setelah membayar, mohon <span className="font-bold">wajib</span> mengirimkan screenshot bukti transfer ke WhatsApp agar pesanan segera diproses.
              </p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gold/5 rounded-2xl border border-gold/10">
              <Info className="h-5 w-5 text-primary shrink-0" />
              <p className="text-xs text-foreground/70 leading-relaxed">
                Pembayaran akan diverifikasi secara manual oleh admin segera setelah bukti pembayaran diterima melalui WhatsApp.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 bg-muted border-t border-border flex flex-col gap-3 shrink-0">
          <Button 
            onClick={handleConfirmPayment}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl h-12 shadow-lg shadow-primary/10"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" /> Sudah Bayar (Sistem)
          </Button>

          <Button 
            variant="outline"
            className="w-full border-green-500 text-green-500 hover:bg-green-500 hover:text-white font-bold rounded-xl h-12"
            asChild
          >
            <a 
              href={`https://wa.me/6281261089400?text=${encodeURIComponent(
                `Halo Admin Azman, saya sudah membayar untuk pesanan saya.\n\n` +
                `📌 *ID Pesanan:* ${order.id}\n` +
                `📦 *Produk:* ${order.productName}\n` +
                `💰 *Nominal:* Rp ${order.amount.toLocaleString('id-ID')}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="h-5 w-5 mr-2" /> Konfirmasi via WhatsApp
            </a>
          </Button>

          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-foreground/40 hover:text-foreground hover:bg-transparent font-medium"
          >
            Nanti Saja
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
